import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { ProductPriceSectionComponent } from '../product-price-section/product-price-section.component';
import { ProductDetailsSectionComponent } from '../product-details-section/product-details-section.component';
import { TranslatePipe } from '@ngx-translate/core';
import { FormArray, FormGroup } from '@angular/forms';
import { filter, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProductGeneralInfoComponent } from '@app/pages/inventory/modal-dialog/inventory-item-modal/product-general/product-general-info.component';
import { InventoryTabKeys } from '@app/utils/enums/inventory-table-columns.enum';
import {
  EditInventoryTab,
  COLLAPSED_HEADER_SCROLL_THRESHOLD,
  ACTIVE_SECTION_THRESHOLD,
} from '@app/utils/constants/inventory-table.constant';
import { InventoryItemFullInformation } from '@app/utils/interfaces/inventory-item.interface';
import { ProductDetailFormControlsType } from '@app/utils/interfaces/product-detail-form-controls.interface';
import { EditInventoryUiService } from '../../services/edit-inventory-ui.service';
import { ProductAvailabilitySectionComponent } from '../product-availability-section/product-availability-section.component';

@Component({
  selector: 'app-edit-inventory-section',
  standalone: true,
  imports: [
    TranslatePipe,
    ProductGeneralInfoComponent,
    ProductPriceSectionComponent,
    ProductAvailabilitySectionComponent,
    ProductDetailsSectionComponent,
  ],
  templateUrl: './edit-inventory-section.component.html',
  styleUrl: './edit-inventory-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditInventorySectionComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly uiService = inject(EditInventoryUiService);
  private readonly cancelScrollTimer = signal(0);

  private lastScrollTop = 0;
  private isScrollingProgrammatically = false;

  protected scrollableContent = viewChild<ElementRef>('scrollableContent');
  protected sectionElements = viewChildren<ElementRef>('sectionElement');

  public visibleTabs = input<EditInventoryTab[]>([]);
  public item = input.required<InventoryItemFullInformation>();
  public form = input.required<FormGroup>();

  protected productDetailsComputed = computed<FormArray<FormGroup<ProductDetailFormControlsType>>>(
    () => {
      return (
        (this.form().get('productDetails') as FormArray<
          FormGroup<ProductDetailFormControlsType>
        >) ?? []
      );
    },
  );

  public readonly SECTIONS = InventoryTabKeys;

  public onScroll(): void {
    const element = this.scrollableContent()?.nativeElement;

    if (!element) {
      return;
    }

    const scrollTop = element.scrollTop;

    const passedThreshold = scrollTop > COLLAPSED_HEADER_SCROLL_THRESHOLD;
    const scrollingDown = scrollTop > this.lastScrollTop;

    this.uiService.setHeaderCollapsed(passedThreshold && scrollingDown);

    this.lastScrollTop = scrollTop;

    if (!this.isScrollingProgrammatically) {
      this.updateActiveTabOnScroll();
    }
  }

  public scrollToSection(sectionKey: InventoryTabKeys): void {
    const sections = this.sectionElements();
    const scrollContainer = this.scrollableContent()?.nativeElement;

    if (!scrollContainer || !sections) {
      return;
    }

    const sectionIndex = this.visibleTabs().findIndex((tab) => tab.key === sectionKey);
    const targetSection = sections[sectionIndex]?.nativeElement;

    if (targetSection) {
      this.isScrollingProgrammatically = true;

      this.cancelScrollTimer.update((v) => v + 1);

      const containerRect = scrollContainer.getBoundingClientRect();
      const sectionRect = targetSection.getBoundingClientRect();
      const offsetTop = sectionRect.top - containerRect.top + scrollContainer.scrollTop;

      scrollContainer.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });

      const currentCancelValue = this.cancelScrollTimer();

      timer(800)
        .pipe(
          filter(() => this.cancelScrollTimer() === currentCancelValue),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          this.isScrollingProgrammatically = false;
        });
    }
  }

  private updateActiveTabOnScroll(): void {
    const sections = this.sectionElements();
    const scrollContainer = this.scrollableContent()?.nativeElement;

    if (!scrollContainer || !sections || sections.length === 0) {
      return;
    }

    const containerTop = scrollContainer.getBoundingClientRect().top;

    let activeIndex = 0;
    let minDistance = Number.MAX_SAFE_INTEGER;

    sections.forEach((section, index) => {
      const element = section.nativeElement;
      const rect = element.getBoundingClientRect();
      const distance = Math.abs(rect.top - containerTop);

      if (distance < minDistance && rect.top <= containerTop + ACTIVE_SECTION_THRESHOLD) {
        minDistance = distance;
        activeIndex = index;
      }
    });

    const visibleTabs = this.visibleTabs();
    if (visibleTabs[activeIndex]) {
      const newActiveTab = visibleTabs[activeIndex].key;

      if (this.uiService.activeTab() !== newActiveTab) {
        this.uiService.setActiveTab(newActiveTab);
      }
    }
  }
}
